import { useState } from "react";
import { toast } from "sonner";
import { Mail, Instagram, MapPin } from "lucide-react";
import { submitContact } from "../lib/api";

const Contact = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitContact(form);
            toast.success("Message sent. We'll be in touch soon.");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch {
            toast.error("Could not send. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="page-contact" className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 py-16 md:py-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
                <div>
                    <p className="text-[11px] tracking-[0.35em] uppercase text-[#9E5A35] mb-4">
                        Contact
                    </p>
                    <h1 className="font-serif-display text-5xl md:text-6xl text-[#0A2342] leading-[1.02]">
                        Get in touch.
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#1A1A1A]/80 max-w-md leading-relaxed">
                        Custom orders, monogrammed fobs, wholesale inquiries —
                        we'd love to hear what you have in mind.
                    </p>

                    <div className="mt-10 space-y-5">
                        <div className="flex items-center gap-4">
                            <span className="w-10 h-10 border border-[#E5E0D8] flex items-center justify-center text-[#0A2342]">
                                <Mail size={16} strokeWidth={1.5} />
                            </span>
                            <a
                                data-testid="contact-email-link"
                                href="mailto:crownandoakcompany@gmail.com"
                                className="text-sm text-[#0A2342] hover:text-[#9E5A35]"
                            >
                                crownandoakcompany@gmail.com
                            </a>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="w-10 h-10 border border-[#E5E0D8] flex items-center justify-center text-[#0A2342]">
                                <Instagram size={16} strokeWidth={1.5} />
                            </span>
                            <a
                                data-testid="contact-instagram-link"
                                href="https://instagram.com/crownandoakcompany"
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-[#0A2342] hover:text-[#9E5A35]"
                            >
                                @crownandoakcompany
                            </a>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="w-10 h-10 border border-[#E5E0D8] flex items-center justify-center text-[#0A2342]">
                                <MapPin size={16} strokeWidth={1.5} />
                            </span>
                            <span
                                data-testid="contact-location"
                                className="text-sm text-[#5C6A79]"
                            >
                                Handcrafted in Singapore
                            </span>
                        </div>
                    </div>
                </div>

                <form
                    data-testid="contact-form"
                    onSubmit={submit}
                    className="space-y-6"
                >
                    <div>
                        <label className="text-[11px] tracking-[0.22em] uppercase text-[#5C6A79]">
                            Name
                        </label>
                        <input
                            data-testid="contact-input-name"
                            required
                            value={form.name}
                            onChange={handle("name")}
                            className="mt-2 w-full bg-transparent border border-[#E5E0D8] px-4 py-3 text-sm focus:outline-none focus:border-[#0A2342]"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] tracking-[0.22em] uppercase text-[#5C6A79]">
                            Email
                        </label>
                        <input
                            data-testid="contact-input-email"
                            required
                            type="email"
                            value={form.email}
                            onChange={handle("email")}
                            className="mt-2 w-full bg-transparent border border-[#E5E0D8] px-4 py-3 text-sm focus:outline-none focus:border-[#0A2342]"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] tracking-[0.22em] uppercase text-[#5C6A79]">
                            Subject
                        </label>
                        <input
                            data-testid="contact-input-subject"
                            value={form.subject}
                            onChange={handle("subject")}
                            className="mt-2 w-full bg-transparent border border-[#E5E0D8] px-4 py-3 text-sm focus:outline-none focus:border-[#0A2342]"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] tracking-[0.22em] uppercase text-[#5C6A79]">
                            Message
                        </label>
                        <textarea
                            data-testid="contact-input-message"
                            required
                            rows={6}
                            value={form.message}
                            onChange={handle("message")}
                            className="mt-2 w-full bg-transparent border border-[#E5E0D8] px-4 py-3 text-sm focus:outline-none focus:border-[#0A2342] resize-none"
                        />
                    </div>
                    <button
                        data-testid="contact-submit-btn"
                        type="submit"
                        disabled={loading}
                        className="btn-primary disabled:opacity-40"
                    >
                        {loading ? "Sending…" : "Send Message"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Contact;
